import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import PageHeader from "@/components/common/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { CheckCircle2, XCircle, ArrowRight, Package, Warehouse } from "lucide-react";
import { fmtDate } from "@/lib/format";

/**
 * Phase 3 · Stores Allocation Queue.
 * Store users see PR-line allocation requests, check cross-warehouse stock and
 * either Allocate (with per-warehouse breakdown) or Deny (auto-forwards the
 * whole PR to the procurement queue).
 */
export default function StoresQueue() {
  const [tab, setTab] = useState("pending");
  const [rows, setRows] = useState([]);
  const [active, setActive] = useState(null);   // allocation row for the drawer
  const [alloc, setAlloc] = useState({});       // { warehouse_id: qty }
  const [denyOpen, setDenyOpen] = useState(false);
  const [denyReason, setDenyReason] = useState("");

  const load = () => api.get("/pr-allocations").then((r) => setRows(r.data || [])).catch(() => setRows([]));
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    if (tab === "pending") return rows.filter((r) => ["pending", "insufficient"].includes(r.status));
    if (tab === "allocated") return rows.filter((r) => ["allocated", "partially_allocated"].includes(r.status));
    if (tab === "forwarded") return rows.filter((r) => r.status === "forwarded_to_procurement");
    return rows;
  }, [rows, tab]);

  const openAllocate = (row) => {
    setActive(row);
    const seed = {};
    (row.warehouse_stock_snapshot || []).forEach((w) => { seed[w.warehouse_id] = 0; });
    setAlloc(seed);
    setDenyOpen(false);
  };

  const totalAllocated = useMemo(() => Object.values(alloc).reduce((a, b) => a + (Number(b) || 0), 0), [alloc]);
  const overflow = active && totalAllocated > active.requested_qty;

  const doAllocate = async () => {
    if (!active) return;
    if (totalAllocated <= 0) return toast.error("Enter at least one warehouse quantity");
    if (overflow) return toast.error("Total exceeds requested quantity");
    const allocated_from = Object.entries(alloc)
      .filter(([_, q]) => Number(q) > 0)
      .map(([warehouse_id, q]) => ({ warehouse_id, qty: Number(q) }));
    try {
      await api.post(`/pr-allocations/${active.id}/allocate`, { allocated_from });
      toast.success("Allocated");
      setActive(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Allocate failed"); }
  };

  const doDeny = async () => {
    if (!active) return;
    if (!denyReason.trim()) return toast.error("Please provide a reason");
    try {
      await api.post(`/pr-allocations/${active.id}/deny`, { reason: denyReason });
      toast.success("PR forwarded to procurement");
      setActive(null); setDenyOpen(false); setDenyReason(""); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Deny failed"); }
  };

  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="stores-queue-page">
      <PageHeader
        eyebrow="Procurement · Phase 3"
        title="Stores Allocation Queue"
        description="Cross-warehouse allocation of goods PRs before they reach Procurement." />

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="rounded-sm">
          <TabsTrigger value="pending" className="rounded-sm" data-testid="sq-tab-pending">Pending ({rows.filter((r) => ["pending", "insufficient"].includes(r.status)).length})</TabsTrigger>
          <TabsTrigger value="allocated" className="rounded-sm" data-testid="sq-tab-allocated">Allocated ({rows.filter((r) => ["allocated", "partially_allocated"].includes(r.status)).length})</TabsTrigger>
          <TabsTrigger value="forwarded" className="rounded-sm" data-testid="sq-tab-forwarded">Forwarded ({rows.filter((r) => r.status === "forwarded_to_procurement").length})</TabsTrigger>
          <TabsTrigger value="all" className="rounded-sm" data-testid="sq-tab-all">All ({rows.length})</TabsTrigger>
        </TabsList>

        <TabsContent value={tab} className="mt-4">
          <div className="card-sharp overflow-hidden">
            <table className="w-full dense">
              <thead>
                <tr>
                  <th>Date</th><th>PR #</th><th>Item</th><th className="text-right">Requested</th><th className="text-right">Available</th><th>Status</th><th></th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 && <tr><td colSpan="7" className="text-center py-10 text-muted-foreground">No rows in this bucket</td></tr>}
                {filtered.map((r) => (
                  <tr key={r.id} data-testid={`sq-row-${r.id}`}>
                    <td className="text-xs">{fmtDate(r.created_at?.slice(0, 10))}</td>
                    <td className="font-mono text-xs">{r.pr_number}</td>
                    <td>{r.item_name}</td>
                    <td className="text-right tabular-nums">{r.requested_qty}</td>
                    <td className="text-right tabular-nums text-emerald-700">{r.total_available}</td>
                    <td className="text-[10px] font-mono uppercase">{r.status.replaceAll("_", " ")}</td>
                    <td className="text-right">
                      {["pending", "insufficient"].includes(r.status) && (
                        <Button size="sm" variant="ghost" className="h-7 text-xs gap-1"
                                  onClick={() => openAllocate(r)}
                                  data-testid={`sq-open-${r.id}`}>
                          <ArrowRight className="w-3 h-3" /> Handle
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>
      </Tabs>

      {/* --- Allocate drawer --- */}
      <Sheet open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <SheetContent side="right" className="w-full sm:max-w-2xl overflow-y-auto space-y-4" data-testid="sq-drawer">
          <SheetHeader>
            <SheetTitle className="font-heading">
              {active?.pr_number} · {active?.item_name}
            </SheetTitle>
          </SheetHeader>
          {active && !denyOpen && (
            <>
              <Card className="rounded-sm p-3 flex items-center gap-4">
                <Package className="w-4 h-4 text-muted-foreground" />
                <div className="text-sm">
                  Requested: <span className="tabular-nums font-medium">{active.requested_qty}</span> ·
                  Available: <span className="tabular-nums font-medium text-emerald-700 ml-1">{active.total_available}</span>
                </div>
              </Card>

              <div>
                <Label className="text-[10px] font-mono uppercase text-muted-foreground">// Allocate from warehouses</Label>
                <div className="mt-2 border rounded-sm overflow-hidden">
                  <table className="w-full dense">
                    <thead>
                      <tr className="text-[10px] font-mono uppercase text-muted-foreground bg-muted/40">
                        <th className="p-2 text-left">Warehouse</th>
                        <th className="p-2 text-right">Available</th>
                        <th className="p-2 text-right">Allocate</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(active.warehouse_stock_snapshot || []).length === 0 && (
                        <tr><td colSpan="3" className="p-4 text-center text-muted-foreground">No stock in any warehouse. Please deny + forward to procurement.</td></tr>
                      )}
                      {(active.warehouse_stock_snapshot || []).map((w) => (
                        <tr key={w.warehouse_id} className="border-t">
                          <td className="p-2 text-sm"><Warehouse className="w-3 h-3 inline mr-1" />{w.name}</td>
                          <td className="p-2 text-right tabular-nums text-emerald-700">{w.available}</td>
                          <td className="p-2 text-right">
                            <Input type="number" value={alloc[w.warehouse_id] ?? 0}
                                     onChange={(e) => setAlloc({ ...alloc, [w.warehouse_id]: Number(e.target.value) })}
                                     className="rounded-sm h-8 w-24 text-right tabular-nums ml-auto"
                                     data-testid={`sq-alloc-${w.warehouse_id}`} />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className={`text-xs mt-2 tabular-nums ${overflow ? "text-red-600" : totalAllocated === active.requested_qty ? "text-emerald-700" : "text-muted-foreground"}`}>
                  {totalAllocated}/{active.requested_qty} allocated {overflow && "· exceeds requested!"}
                </div>
              </div>

              <div className="flex justify-between gap-2 pt-2">
                <Button variant="outline" className="rounded-sm gap-1" onClick={() => setDenyOpen(true)} data-testid="sq-open-deny">
                  <XCircle className="w-4 h-4" /> Deny + forward to procurement
                </Button>
                <Button className="rounded-sm gap-1 bg-accent hover:bg-accent/90 text-white" onClick={doAllocate} disabled={overflow || totalAllocated === 0} data-testid="sq-allocate-btn">
                  <CheckCircle2 className="w-4 h-4" /> Allocate
                </Button>
              </div>
            </>
          )}

          {active && denyOpen && (
            <div className="space-y-3">
              <div className="text-sm">You are about to <strong className="text-red-600">deny</strong> stores allocation for <span className="font-mono">{active.pr_number}</span>. The PR will be auto-forwarded to procurement.</div>
              <Label>Reason</Label>
              <Textarea value={denyReason} onChange={(e) => setDenyReason(e.target.value)} className="rounded-sm" placeholder="e.g. Not in stock across any warehouse" data-testid="sq-deny-reason" />
              <div className="flex justify-end gap-2">
                <Button variant="outline" className="rounded-sm" onClick={() => setDenyOpen(false)}>Back</Button>
                <Button className="rounded-sm bg-red-600 hover:bg-red-700 text-white" onClick={doDeny} data-testid="sq-deny-confirm">Forward to procurement</Button>
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
